import { Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { queryClient } from '../../../lib/query-client';
import { useDeleteDiscount } from '../hooks/use-discounts-delete';
import { QK_DISCOUNTS_LIST } from '../hooks/use-discounts-list';
import type { Discount } from '../types';

type DiscountsDeleteModalProps = {
  discount: Discount | null;
  isOpen: boolean;
  closeModal: () => void;
};

export function DiscountsDeleteModal({ discount, isOpen, closeModal }: DiscountsDeleteModalProps) {
  const { notificationHandler } = useHandlers();
  const deleteDiscount = useDeleteDiscount();

  if (!discount) {
    return null;
  }

  function handleDelete(discount: Discount) {
    deleteDiscount.mutate(discount.id, {
      onError: (_error) => {
        notificationHandler.error(
          t('features:discounts.components.discounts_delete_modal.status.errors.delete_failed'),
        );
      },
      onSuccess: () => {
        notificationHandler.success(
          t(
            'features:discounts.components.discounts_delete_modal.status.success.delete_succeeded',
            { name: discount.name },
          ),
        );

        queryClient.invalidateQueries({ queryKey: QK_DISCOUNTS_LIST() });
      },
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      primaryAction={{
        text: t('buttons:delete'),
        onClick: () => {
          handleDelete(discount);
          closeModal();
        },
        danger: true,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: closeModal,
      }}
    >
      <Modal.Header>{t('features:discounts.components.discounts_delete_modal.title')}</Modal.Header>
      <Modal.Body>
        <Text variant="secondary" size="sm">
          {t('features:discounts.components.discounts_delete_modal.description', {
            name: discount.name,
          })}
        </Text>
      </Modal.Body>
    </Modal>
  );
}
